import { formatCents, localDate, zonedToUtc } from '@plataforma/shared';
import { lang, locale } from './i18n';

export const DEFAULT_TZ = 'America/New_York';

function d(v: string | Date) {
  return typeof v === 'string' ? new Date(v) : v;
}

export function fmtDate(v: string | Date | null | undefined, tz = DEFAULT_TZ, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (!v) return '';
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, ...opts }).format(d(v));
}

export function fmtDateLong(v: string | Date | null | undefined, tz = DEFAULT_TZ) {
  return fmtDate(v, tz, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export function fmtTime(v: string | Date | null | undefined, tz = DEFAULT_TZ) {
  if (!v) return '';
  return new Intl.DateTimeFormat(locale(), { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d(v));
}

export function fmtDateTime(v: string | Date | null | undefined, tz = DEFAULT_TZ) {
  if (!v) return '';
  return `${fmtDate(v, tz)} · ${fmtTime(v, tz)}`;
}

/** Fecha de calendario "AAAA-MM-DD" sin convertir de zona (cumpleaños, vencimientos). */
export function fmtDateOnly(s: string | null | undefined, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (!s) return '';
  const [y, m, day] = s.split('-').map(Number);
  return new Intl.DateTimeFormat(locale(), { timeZone: 'UTC', ...opts }).format(new Date(Date.UTC(y!, m! - 1, day!)));
}

export function money(cents: number | null | undefined, currency = 'USD') {
  return formatCents(cents ?? 0, currency, locale());
}

export function relative(v: string | Date) {
  const diff = (d(v).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(lang(), { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86400), 'day');
}

/** "AAAA-MM-DDTHH:MM" para <input type="datetime-local"> en la zona indicada. */
export function toLocalInput(v: string | Date, tz = DEFAULT_TZ) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d(v));
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
}

export function minutes(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}:${String(s).padStart(2, '0')}` : `${m} min`;
}

/** Convierte el valor de <input type="datetime-local"> (hora local del campus) a ISO UTC. */
export function fromLocalInput(value: string, tz = DEFAULT_TZ): string {
  const [date, time] = value.split('T');
  return zonedToUtc(date!, (time ?? '00:00').slice(0, 5), tz);
}

/** Fecha de hoy (AAAA-MM-DD) en la zona indicada. */
export function todayIn(tz = DEFAULT_TZ): string {
  return localDate(new Date(), tz);
}

/** Fecha de calendario (AAAA-MM-DD) de un instante en la zona indicada. */
export function localDateOf(v: string | Date, tz = DEFAULT_TZ): string {
  return localDate(v, tz);
}

/** Solo la primera letra en mayúscula: "miércoles, 21 de octubre" → "Miércoles, 21 de octubre". */
export function capFirst(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase(lang()) + s.slice(1) : s;
}

/** Número con separadores según el idioma: 1,234. */
export function num(n: number | null | undefined) {
  return (n ?? 0).toLocaleString(locale());
}
